import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import { kuyrukKaydiSemasi, type KuyrukKaydi } from "@koclukpanel/contracts";
import type { KuyrukDeposu } from "./kuyruk";

const VERITABANI = "koclukpanel-ogrenci";
const SURUM = 3;
const DEPO = "senkron-kuyrugu";
const KARANTINA = "sahipsiz-kayitlar";
const SAYACLAR = "sira-sayaclari";

interface KuyrukVeritabani extends DBSchema {
  "senkron-kuyrugu": { key: string; value: KuyrukKaydi; indexes: { sahipKullaniciId: string } };
  "sahipsiz-kayitlar": { key: string; value: unknown };
  "sira-sayaclari": { key: string; value: number };
}

/**
 * Kuyrugun IndexedDB uygulamasi.
 *
 * Kuyruk mantigi bu dosyayi BILMEZ; yalnizca `KuyrukDeposu` arayuzunu bilir.
 * Butun kurallar bellek deposuyla test edildi (kuyruk.test.ts); burasi
 * kalici saklama ve sahiplik kaniti sonrasi atomik geri alma.
 *
 * 14.09.2026: v2 eski sahipsiz kayitlari silmeden karantinaya alir;
 * taze sunucu verisiyle kanitlanirsa sahibine geri doner. Son girise
 * atamak paylasilan telefonda baska ogrenciye veri yazabilirdi.
 *
 * Neden IndexedDB: localStorage senkron calisir ve ana is parcacigini
 * kilitler; ayrica 5 MB sinirlidir ve yalnizca metin saklar.
 */
export class IndexedDbDeposu implements KuyrukDeposu {
  private db: Promise<IDBPDatabase<KuyrukVeritabani>> | null = null;

  private ac(): Promise<IDBPDatabase<KuyrukVeritabani>> {
    if (this.db) return this.db;
    let engellendi = false;
    let engeliBildir!: (hata: Error) => void;
    const engel = new Promise<never>((_coz, reddet) => {
      engeliBildir = reddet;
    });
    const acilis = openDB<KuyrukVeritabani>(VERITABANI, SURUM, {
      blocked: () => {
        engellendi = true;
        engeliBildir(
          new Error(
            "Kayıt deposu güncellenemedi. Lütfen uygulamanın diğer sekmelerini kapatıp yeniden deneyiniz. Kayıtlarınız korunuyor.",
          ),
        );
      },
      upgrade(db, _eskiSurum, _yeniSurum, islem) {
        const depo = db.objectStoreNames.contains(DEPO)
          ? islem.objectStore(DEPO)
          : db.createObjectStore(DEPO, { keyPath: "istemciKayitId" });
        if (!depo.indexNames.contains("sahipKullaniciId"))
          depo.createIndex("sahipKullaniciId", "sahipKullaniciId");
        if (!db.objectStoreNames.contains(KARANTINA)) db.createObjectStore(KARANTINA);
        if (!db.objectStoreNames.contains(SAYACLAR)) db.createObjectStore(SAYACLAR);
        // F-STV-11 — eski kaydın sahibini son girişten tahmin etmek başka öğrenciye veri yazar.
        void (async () => {
          let imlec = await depo.openCursor();
          while (imlec) {
            if (!kuyrukKaydiSemasi.safeParse(imlec.value).success) {
              await islem.objectStore(KARANTINA).put(imlec.value, imlec.primaryKey);
              await imlec.delete();
            }
            imlec = await imlec.continue();
          }
        })().catch(() => {
          islem.abort();
        });
      },
      blocking: () => {
        void acilis.then((db) => db.close());
        this.db = null;
      },
      terminated: () => {
        this.db = null;
      },
    });
    void acilis.then(
      (db) => {
        if (engellendi) db.close();
      },
      () => undefined,
    );
    this.db = Promise.race([acilis, engel]).catch((hata: unknown) => {
      this.db = null;
      throw hata;
    });
    return this.db;
  }

  async hepsi(): Promise<KuyrukKaydi[]> {
    return (await this.ac()).getAll(DEPO);
  }

  async ekle(kayit: Omit<KuyrukKaydi, "siraNo">): Promise<KuyrukKaydi> {
    const db = await this.ac();
    // İki sekmenin readwrite işlemleri aynı depolar üzerinde sırayla yürür.
    // Kayıtlar silinse veya uygulama yeniden açılsa da sayaç geri alınmaz.
    const islem = db.transaction([DEPO, SAYACLAR], "readwrite");
    try {
      const sayaclar = islem.objectStore(SAYACLAR);
      const siraNo = ((await sayaclar.get(kayit.sahipKullaniciId)) ?? 0) + 1;
      if (!Number.isSafeInteger(siraNo)) throw new Error("Kuyruk sıra sayacı sınırına ulaştı.");
      const yeni = { ...kayit, siraNo };
      await sayaclar.put(siraNo, kayit.sahipKullaniciId);
      await islem.objectStore(DEPO).add(yeni);
      await islem.done;
      return yeni;
    } catch (hata) {
      try {
        islem.abort();
      } catch {
        // İşlem zaten sonlanmış olabilir.
      }
      await islem.done.catch(() => undefined);
      throw hata;
    }
  }

  async karantinadakiler(): Promise<unknown[]> {
    return (await this.ac()).getAll(KARANTINA);
  }

  /**
   * F-STV-11 — 14.09.2026'da eklendi: eski kaydı son girişe atamak yerine
   * taze sunucu verisiyle kanıtlanan sahibine döndürürüz. İki depo aynı
   * işlemde değişir; kapanma veya iki sekme aynı kaydı çoğaltamaz/kaybedemez.
   * Son yetki kararı yine sunucunun kayıt bazlı kapsam kontrolündedir.
   */
  async karantinayiGeriAl(
    sahip: string,
    ogrenciId: string,
    gorevler: ReadonlySet<string>,
    gecerli: () => boolean,
  ): Promise<void> {
    const db = await this.ac();
    if (!gecerli()) return;
    const islem = db.transaction([DEPO, KARANTINA], "readwrite");
    const depo = islem.objectStore(DEPO);
    let imlec = await islem.objectStore(KARANTINA).openCursor();
    while (imlec) {
      if (!gecerli()) {
        islem.abort();
        await islem.done.catch(() => undefined);
        return;
      }
      const eski = kuyrukKaydiSemasi.omit({ sahipKullaniciId: true }).safeParse(imlec.value);
      const ham = imlec.value as { sahipKullaniciId?: unknown } | null;
      if (eski.success && ham?.sahipKullaniciId === undefined) {
        const kayit = eski.data;
        const kanitli =
          kayit.islem === "konu.durum.degistir"
            ? kayit.govde.ogrenciId === ogrenciId
            : typeof kayit.govde.gorevId === "string" && gorevler.has(kayit.govde.gorevId);
        if (kanitli && !(await depo.get(kayit.istemciKayitId))) {
          await depo.put({
            ...kayit,
            sahipKullaniciId: sahip,
            durum: kayit.durum === "gonderiliyor" ? "bekliyor" : kayit.durum,
            gonderimPartisiId: undefined,
          });
          await imlec.delete();
        }
      }
      imlec = await imlec.continue();
    }
    if (!gecerli()) islem.abort();
    await islem.done;
  }
  async yaz(kayit: KuyrukKaydi): Promise<void> {
    await (await this.ac()).put(DEPO, kayit);
  }
  async sil(istemciKayitId: string): Promise<void> {
    await (await this.ac()).delete(DEPO, istemciKayitId);
  }
}

// 14.09.2026: Vite önbellek kalıbı da kimlik üretir; saf yardımcı ayrı
// dosyada kalır ki yapılandırma IndexedDB/contracts bağımlılığı yüklemesin.
export { kimlikUret } from "./kimlik";
